import { AxiosError, HttpStatusCode } from 'axios';
import { initAxiosHeaders, axiosClient } from '~/common/js/axios';
import { KYC_ERROR_CODE } from '~~/common/js/const';
import futuresTransforResp, { futuresErrorCodeArray } from '~/common/js/futures-transfor-resp';
import { verifyKycByErrorCode } from '~/common/js/helper';
import { AXIOS_ERROR_CODE_MAP, MUTED_ERR_SETS } from '~/frontend-shared/helper/const';
import { debounceMessage } from '~/frontend-shared/helper/util';
import { isEmpty } from 'lodash-es';
import { setAppLoginInfo } from '~/common/js/storeUtil';
import { useIndexStore } from '~/store';

// 有些错误特殊不要报出来
// error2505是抽奖分享时返回的不能再增加抽奖次数的错误，所以不需要错误提示小弹框
// error2300是提现页面下拉框点击清除后显示未找到搜索币种，所以不需要错误提示小弹框
// error3202是galaxy star页面还不是BitMart之星则弹回galaxy
// error16001是后端故意不返回手续费列表，怕做市商误解手续费。先忽略掉这个错误消息就好了，不要弹出错误信息。
// error14351是法币提示需要MFA验证
// 194103 194108 是法币下单超时的错误吗，不需要全局提示
// 36037 nft-商品不在购买时间内
// 36033 nft-商品库存不够或已被他人买走
// 33133 交易错误码，计划委托超过20单，这个要用业务中的弹出框，不要用全局的

// P2P业务 部分error不展示通用报错、特殊处理
export const CANCEL_PIGEON_CODE = 48238;

const p2pSpecialMessageList = [
  // 下单
  48003,
  48319,
  // im token 获取失效
  60001,
  CANCEL_PIGEON_CODE,
  48114, // 创建订单 kyc unique trade 
  48239, // 创建委托单 
  48228, // 当前货币在该地区不允许交易
];


const cardsSpecialMessageList = [
  55011, // 未填生日信息 绑卡不能通过的特殊报错
]; 

// 不要再加，如果要加表明后端接口不规范
const noMessageList = [
  -23, -34, -37, -2505, -2300, -2705, -3202, 16001, 194103, 194108, 36037, 36033, 33122, 33117, 33118, 33124, 33125,
  33126, 33127, 33128, 33129, 33130, 33133, 34004, 34006, 40046, 33138, 48106, 48228, 34066
].concat(p2pSpecialMessageList).concat(cardsSpecialMessageList);


// 强提示， 弹框
const strongMessageList = [-2301, 12503, 32092, 32091, 33001, 12507, 25220, 25221, 34008, 34009];

export default defineNuxtPlugin((ctx) => {
  // console.log('defineNuxtPlugin setup', ctx, ctx.i18n);
  const indexStore = useIndexStore();
  // console.log(indexStore);
  initAxiosHeaders();

  axiosClient.interceptors.response.use(
    (res) => {
      const { config, data: serviceResponse, status } = res;
    
      if (status === HttpStatusCode.Ok) {
        if (!serviceResponse?.success && +serviceResponse.code !== 0) {
          // 验证是否走kyc认证流程
          if (
            // @ts-ignore
            (serviceResponse.errno && KYC_ERROR_CODE[serviceResponse.errno]) ||
            // @ts-ignore
            (serviceResponse.code && KYC_ERROR_CODE[serviceResponse.code])
          ) {
            const verified = verifyKycByErrorCode(serviceResponse.errno || serviceResponse.code, {
              extraData: serviceResponse.data,
            });
            if (!verified) {
              return serviceResponse;
            }
          }

          // 报错处理 未登录不报错了
          if (+serviceResponse.code !== -6 && +serviceResponse.code !== -36) {
            // 有些错误特殊不要报出来
            // error2505是抽奖分享时返回的不能再增加抽奖次数的错误，所以不需要错误提示小弹框
            // error2300是提现页面下拉框点击清除后显示未找到搜索币种，所以不需要错误提示小弹框
            // error3202是galaxy star页面还不是BitMart之星则弹回galaxy
            // error16001是后端故意不返回手续费列表，怕做市商误解手续费。先忽略掉这个错误消息就好了，不要弹出错误信息。
            // error14351是法币提示需要MFA验证
            // 194103 194108 是法币下单超时的错误吗，不需要全局提示
            if (
              !serviceResponse.code &&
              serviceResponse.errno &&
              futuresErrorCodeArray.includes(serviceResponse.errno)
            ) {
              // 说明是合约接口 没有经过平台组处理的接口回参
              return futuresTransforResp(serviceResponse, config);
            }

            if (!serviceResponse.code || isNaN(+serviceResponse.code)) {
              // 如果不存在 code, 提示 "网络错误，请稍后再试"
              const msg =
                serviceResponse.msg ||
                serviceResponse.errno ||
                serviceResponse.message ||
                serviceResponse.statusText ||
                serviceResponse.status;
              let errorMsg = ctx.i18n?.global.t('error_code.e_4');
              if (msg) {
                errorMsg += `[${JSON.stringify(msg)}]`;
              }
              debounceMessage({
                message: errorMsg,
                level: 'error',
              });
              return;
            }
            const code = +serviceResponse.code;
            if (
              !KYC_ERROR_CODE[code] &&
              !noMessageList.includes(code) &&
              !config?.headers?.noMessageList?.includes(code) &&
              !config?.headers?.noMessageList?.includes(`${code}`)
            ) {
              let errorMsg = null;
              const i18nStr = `error_code.e_${Math.abs(code)}`;
              if (ctx.i18n?.global.te(i18nStr)) {
                // 有当前的错误码
                if (serviceResponse.data && serviceResponse.data.errReplace) {
                  errorMsg = ctx.i18n?.global.t(i18nStr, serviceResponse.data.errReplace);
                } else {
                  errorMsg = ctx.i18n?.global.t(i18nStr);
                }
                errorMsg += `[${code}]`;
              } else {
                errorMsg = `[${code}]`;
                if (serviceResponse.msg) {
                  errorMsg = `${serviceResponse.msg}${errorMsg}`;
                }
              }

              if(Math.abs(code) === 2002){
                const [authAccountType, operationBizType] = serviceResponse.msg.split(' cannot use ')
                const act = ctx.i18n?.global.t(`auth-account-type.${authAccountType.replaceAll(' ', '_')}`)
                const opbt = ctx.i18n?.global.t(`operation-biz-type.${operationBizType}`)
                errorMsg = ctx.i18n?.global.t(i18nStr, [act, opbt])
              }

              if (strongMessageList.includes(code)) {
                ElMessageBox.confirm(errorMsg, '', {
                  confirmButtonText: ctx.i18n?.global.t('api.confirm'),
                  type: 'warning',
                });
              } else {
                // Message.closeAll()
                debounceMessage({
                  message: errorMsg,
                  level: 'error',
                  throttleTime: 3000,
                });
              }
            }
          } else {
            indexStore.SET_CLEAN_INFO();

            const router = useRouter();
            // 如果已经在登陆页，则不处理
            if (ctx.$router.currentRoute.value.name === 'login-lang') {
              return serviceResponse;
            }
            // 2022-06-20
            // 判断用户场景，如果在ios或安卓，则跳到对应的原生登陆页
            if (!!window.jsBridge && window.jsBridge?.toLogin) {
              window.jsBridge.toLogin((params) => {
                // 健壮代码，ios的JSON解析不合法
                // 以后可以移除
                params = params.replace(/(Optional\("(.*)"\)\))/, '$2');
                const paramsObj = JSON.parse(params);
                if (paramsObj?.success) setAppLoginInfo(indexStore, paramsObj);
              });

              return serviceResponse;
            }
            
            const routeParam = {
              name: ctx.$router.currentRoute.value.path.includes('/nftmarket') ? 'login-nft-lang' : 'login-lang',
              params: {
                lang: indexStore.normalizedLocaleWithCenterLine || 'en-US',
              },
              query: {
                goto: ctx.$router.currentRoute.value.name,
              },
            } as any;
            if (isEmpty(ctx.$router.currentRoute.value.query)) {
              delete routeParam.query.others;
            }else{
              routeParam.query.others = JSON.stringify(ctx.$router.currentRoute.value.query);
            }
            const path = ctx.$router.currentRoute.value.path;
            if (!(/^\/trade(\/.*)?$/.test(path))) {
              // https://github.com/vuejs/vue-router/issues/2873
              // https://github.com/vuejs/vue-router/issues/2881#issuecomment-520554378
              ctx.$router.push(routeParam);
            }
          }
        }
        return serviceResponse;
      }

      return res;
    },
    (err: AxiosError) => {
      switch (err.code) {
        case AxiosError.ERR_NETWORK:
        case AxiosError.ETIMEDOUT:
        case AxiosError.ECONNABORTED:
          // if (err.config?.method?.toLowerCase() === 'get' || MUTED_ERR_SETS.has(err.config?.url)) {
          if (err.config?.url && MUTED_ERR_SETS.has(err.config.url)) {
            return;
          }
          if (err.code === AxiosError.ERR_NETWORK) {
            debounceMessage({
              message: ctx.i18n?.global.t('login.a8') + ` [${AXIOS_ERROR_CODE_MAP.get(err.code)}]`,
              level: 'error',
            });
          } else {
            debounceMessage({
              message: ctx.i18n?.global.t('login.a9') + ` [${AXIOS_ERROR_CODE_MAP.get(err.code)}]`,
              level: 'error',
            });
          }
          break;
        case AxiosError.ERR_BAD_RESPONSE:
        case AxiosError.ERR_BAD_REQUEST:
        default:
          break;
      }
    }
  );
});
